import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { formatBytes, resolveAttachmentHref } from "../../utils/mailUtils";
import { SERVER_URL, AUTH_STORAGE_KEY, clearAuthAndRedirect } from "../../socket";
import { updateMailStatus } from "../../api/mailApi";

function MailPreview({
  mail,
  onDelete,
  onReply,
  onReplyAll,
  onForward,
  onStatusUpdated,
  onOpenComposeModal,
  activeFolder = "inbox",
}) {
  const [previewTab, setPreviewTab] = useState("message");
  const [showHeaderDetails, setShowHeaderDetails] = useState(false);
  const headerDetailsRef = useRef(null);
  const headerToggleRef = useRef(null);
  const [showOptions, setShowOptions] = useState(false);
  const optionsRef = useRef(null);
  const optionsToggleRef = useRef(null);
  const [showContactCard, setShowContactCard] = useState(false);
  const contactAnchorRef = useRef(null);
  const [contactActionMsg, setContactActionMsg] = useState("");
  const contactHideTimeoutRef = useRef(null);
  const [isContact, setIsContact] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const navigate = useNavigate();

  const handlePrint = () => {
    const buildDoc = () => {
      const title = (mail.subject || "Message").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const from = (mail.from || "").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const date = (mail.sentAt || mail.date) ? new Date(mail.sentAt || mail.date).toString() : "";
      return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>${title}</title>
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial; margin: 18px; color: #111; }
    .print-header { margin-bottom: 18px; }
    .print-subject { font-size: 18px; font-weight: 700; margin-bottom: 6px; }
    .print-meta { color: #555; font-size: 13px; margin-bottom: 10px; }
    .print-body { font-size: 14px; line-height: 1.45; }
    img { max-width: 100%; height: auto; }
    @media print { body { margin: 6mm; } }
  </style>
</head>
<body>
  <div class="print-header">
    <div class="print-subject">${title}</div>
    <div class="print-meta">From: ${from} ${date ? `• ${date}` : ""}</div>
  </div>
  <div class="print-body">${mail.body && mail.body.trim() ? mail.body : '<em>(No content)</em>'}</div>
</body>
</html>`;
    };

    const doc = buildDoc();

    // Prefer the hidden iframe approach first to avoid showing a visible blank window.
    try {
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.style.opacity = "0";
      iframe.style.pointerEvents = "none";
      iframe.setAttribute("aria-hidden", "true");
      document.body.appendChild(iframe);

      const idoc = iframe.contentDocument || iframe.contentWindow.document;
      idoc.open();
      idoc.write(doc);
      idoc.close();

      let printedIframe = false;
      const doPrint = () => {
        if (printedIframe) return;
        printedIframe = true;
        try {
          const win = iframe.contentWindow || window;
          try { win.focus(); } catch (e) {}
          try { win.print(); } catch (e) {}
        } finally {
          // cleanup
          setTimeout(() => {
            try { document.body.removeChild(iframe); } catch (e) {}
          }, 800);
        }
      };

      iframe.onload = () => setTimeout(doPrint, 300);
      // some browsers may not fire onload; ensure a single fallback call
      setTimeout(doPrint, 800);
      return;
    } catch (err) {
      console.warn("Iframe print failed, will try window.open fallback", err);
    }

    // If iframe approach fails, fall back to opening a new window.
    try {
      const printWindow = window.open("", "_blank", "noopener,noreferrer,width=900,height=700");
      if (printWindow) {
        let printed = false;
        printWindow.document.open();
        printWindow.document.write(doc);
        printWindow.document.close();

        const clearAndPrint = () => {
          if (printed) return;
          printed = true;
          try { printWindow.focus(); } catch (e) {}
          try { printWindow.print(); } catch (e) {}
        };

        // Poll for ready state a short while, then fall back to timeout
        const interval = setInterval(() => {
          try {
            const ready = printWindow.document && printWindow.document.readyState;
            if (ready === "complete" || ready === "interactive") {
              clearInterval(interval);
              clearAndPrint();
            }
          } catch (e) {}
        }, 200);

        // Ensure we call print at least once after delay
        setTimeout(() => {
          clearInterval(interval);
          clearAndPrint();
        }, 2000);

        return;
      }
    } catch (err) {
      console.error("Print failed (both iframe and window attempts)", err);
      alert("Unable to print message. Check your browser settings.");
    }
  };

  const attachments = useMemo(() => {
    if (!mail) return [];
    if (Array.isArray(mail.attachments) && mail.attachments.length) {
      return mail.attachments
        .filter((item) => item && typeof item === "object" && item.url)
        .map((item, index) => ({
          id: item.id || `${mail.id || "mail"}-att-${index + 1}`,
          url: item.url,
          name: item.name || item.filename || `Attachment ${index + 1}`,
          mimeType: item.mimeType || item.contentType || "",
          size: Number.isFinite(Number(item.size)) ? Number(item.size) : null,
        }));
    }
    if (mail?.attachmentUrl) {
      return [
        {
          id: `${mail.id || "mail"}-att-1`,
          url: mail.attachmentUrl,
          name: mail.attachmentName || "Attachment",
          mimeType: mail.attachmentMime || "",
          size: Number.isFinite(Number(mail.attachmentSize)) ? Number(mail.attachmentSize) : null,
        },
      ];
    }
    return [];
  }, [mail]);

  useEffect(() => {
    setPreviewTab("message");
    setShowHeaderDetails(false);
  }, [mail?.id]);

  const formatDate = (value) => {
    if (!value) return "";
    try {
      const dt = new Date(value);
      return dt.toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
      });
    } catch {
      return String(value);
    }
  };

  const formatRecipientValue = (value) => {
    if (value == null || value === "") return "";

    if (Array.isArray(value)) {
      const parts = value
        .map((entry) => {
          if (typeof entry === "string") return entry.trim();
          if (entry && typeof entry === "object") {
            return entry.address || entry.email || entry.value || entry.recipient || entry.mail || "";
          }
          return "";
        })
        .filter(Boolean);
      return parts.join(", ");
    }

    if (typeof value === "object") {
      return value.address || value.email || value.value || value.recipient || value.mail || "";
    }

    return String(value);
  };

  const handleAddContact = async (email) => {
    setContactActionMsg("");
    try {
      // only attempt server save if user is authenticated
      let authHeaders = undefined;
      try {
        const raw = localStorage.getItem(AUTH_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        const token = parsed?.token;
        if (token) authHeaders = { Authorization: `Bearer ${token}` };
      } catch (e) {}

      if (authHeaders) {
        const res = await axios.post(`${SERVER_URL}/api/contacts`, { contactId: email, alias: (profileName || mail.fromName || "") }, { headers: authHeaders });
      } else {
        // save locally and ask user to sign in to sync
        setContactActionMsg("Saved locally — sign in to sync to server");
        setIsContact(true);
        try {
          const raw = localStorage.getItem("mail-contacts");
          const parsed = raw ? JSON.parse(raw) : [];
          const normalized = String(email || "").toLowerCase();
          const exists = Array.isArray(parsed) && parsed.some((c) => {
            const emails = Array.isArray(c.emails) ? c.emails.map(e => String(e).toLowerCase()) : (c.email ? [String(c.email).toLowerCase()] : []);
            return emails.includes(normalized) || String(c.contactId || "").toLowerCase() === normalized;
          });
          if (!exists) {
            const entry = {
              id: Date.now().toString(),
              emails: [normalized],
              name: (profileName || mail.fromName || "").trim(),
              group: "",
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
            const next = Array.isArray(parsed) ? [...parsed, entry] : [entry];
            try { localStorage.setItem("mail-contacts", JSON.stringify(next)); } catch (e) {}
            try { window.dispatchEvent(new CustomEvent("contacts-updated", { detail: { contactId: normalized, alias: entry.name, name: entry.name } })); } catch (e) {}
          }
        } catch (e) {}
        setTimeout(() => setContactActionMsg(""), 2000);
        return;
      }
      setContactActionMsg("Contact added");
      setIsContact(true);
      // also persist to local contacts list and notify other components
      try {
        const raw = localStorage.getItem("mail-contacts");
        const parsed = raw ? JSON.parse(raw) : [];
        const normalized = String(email || "").toLowerCase();
        const exists = Array.isArray(parsed) && parsed.some((c) => {
          const emails = Array.isArray(c.emails) ? c.emails.map(e => String(e).toLowerCase()) : (c.email ? [String(c.email).toLowerCase()] : []);
          return emails.includes(normalized) || String(c.contactId || "").toLowerCase() === normalized;
        });
        if (!exists) {
          const entry = {
            id: Date.now().toString(),
            emails: [normalized],
            name: (profileName || mail.fromName || "").trim(),
            group: "",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          const next = Array.isArray(parsed) ? [...parsed, entry] : [entry];
          try { localStorage.setItem("mail-contacts", JSON.stringify(next)); } catch (e) {}
          // notify other components (useContacts listens for this)
          try { window.dispatchEvent(new CustomEvent("contacts-updated", { detail: { contactId: normalized, alias: entry.name, name: entry.name } })); } catch (e) {}
        } else {
          // still notify to allow list refresh
          try { window.dispatchEvent(new CustomEvent("contacts-updated", { detail: null })); } catch (e) {}
        }
      } catch (e) {
        // ignore local save errors
      }
      setTimeout(() => setContactActionMsg(""), 2000);
    } catch (err) {
      const msg = err?.response?.data?.error || "Failed to add contact";
      setContactActionMsg(msg);
      setTimeout(() => setContactActionMsg(""), 3000);
    }
  };

  const handleSendMail = (email) => {
    if (typeof onOpenComposeModal === "function") {
      onOpenComposeModal(email);
      return;
    }
    navigate(`/kmail/compose?to=${encodeURIComponent(email)}`);
  };

  const handleChat = (email) => {
    try {
      navigate("/kmail/Krivachat", { state: { email, name: email.split("@")[0] } });
    } catch (err) {
      console.error("Failed to open chat screen", err);
    }
    window.dispatchEvent(new CustomEvent("open-quick-chat", { detail: { email } }));
  };

  // Fetch user's contacts and profile suggestion when mail changes
  useEffect(() => {
    if (!mail || !mail.from) return;
    let mounted = true;

    (async () => {
      try {
        // get contacts and check if sender already added
        let authHeaders = undefined;
        try {
          const raw = localStorage.getItem(AUTH_STORAGE_KEY);
          const parsed = raw ? JSON.parse(raw) : null;
          const token = parsed?.token;
          if (token) authHeaders = { Authorization: `Bearer ${token}` };
        } catch (e) {}

        let contactsRes;
        try {
          contactsRes = await axios.get(`${SERVER_URL}/api/contacts`, { headers: authHeaders });
        } catch (err) {
          if (err?.response?.status === 401) {
            clearAuthAndRedirect();
            return;
          }
          throw err;
        }
        if (!mounted) return;
        const contacts = contactsRes.data?.contacts || [];
        const found = contacts.some((c) => String(c.contactId || "").toLowerCase() === String(mail.from || "").toLowerCase());
        setIsContact(Boolean(found));

        // try to resolve profile fullName via suggest endpoint
        try {
          const q = encodeURIComponent(mail.from || "");
          let usersRes;
          try {
            usersRes = await axios.get(`${SERVER_URL}/api/users/suggest?q=${q}`, { headers: authHeaders });
          } catch (err) {
            if (err?.response?.status === 401) {
              clearAuthAndRedirect();
              return;
            }
            throw err;
          }
          if (!mounted) return;
          const users = usersRes.data?.users || [];
          if (users.length > 0) {
            setProfileName(users[0].fullName || "");
            setProfileImage(users[0].profileImageUrl || "");
          } else {
            setProfileName("");
            setProfileImage("");
          }
        } catch (e) {
          setProfileName("");
          setProfileImage("");
        }
      } catch (err) {
        // ignore
      }
    })();

    return () => { mounted = false; };
  }, [mail?.id, mail?.from]);

  useEffect(() => {
    if (previewTab === "attachment" && attachments.length === 0) {
      setPreviewTab("message");
    }
  }, [previewTab, attachments.length]);

  useEffect(() => {
    if (!showHeaderDetails) return undefined;
    const handleClickOutside = (event) => {
      const detailsNode = headerDetailsRef.current;
      const toggleNode = headerToggleRef.current;
      if (detailsNode && detailsNode.contains(event.target)) return;
      if (toggleNode && toggleNode.contains(event.target)) return;
      setShowHeaderDetails(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setShowHeaderDetails(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showHeaderDetails]);

  useEffect(() => {
    if (!showOptions) return undefined;
    const handleClickOutside = (event) => {
      const node = optionsRef.current;
      const toggleNode = optionsToggleRef.current;
      if (node && node.contains(event.target)) return;
      if (toggleNode && toggleNode.contains(event.target)) return;
      setShowOptions(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setShowOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showOptions]);

  // cleanup for contact popover hide timeout
  useEffect(() => {
    return () => {
      if (contactHideTimeoutRef.current) {
        clearTimeout(contactHideTimeoutRef.current);
        contactHideTimeoutRef.current = null;
      }
    };
  }, []);

  if (!mail) {
    return <div className="mail-empty">Select a message to read it.</div>;
  }

  return (
    <div className="mail-preview">
      <div className="mail-preview-head">
    <div className="mail-preview-head-left">
      <div className="mail-preview-subject">{mail.subject}</div>

      <div className="mail-preview-meta">
        <span className="mail-meta-label">From:</span>
        <span
          className="mail-meta-value mail-contact-anchor"
          ref={contactAnchorRef}
          onMouseEnter={() => {
            if (contactHideTimeoutRef.current) {
              clearTimeout(contactHideTimeoutRef.current);
              contactHideTimeoutRef.current = null;
            }
            setShowContactCard(true);
          }}
          onMouseLeave={() => {
            // delay hiding so mouse can move into the popover
            contactHideTimeoutRef.current = setTimeout(() => {
              setShowContactCard(false);
              contactHideTimeoutRef.current = null;
            }, 220);
          }}
          onClick={() => {
            if (contactHideTimeoutRef.current) {
              clearTimeout(contactHideTimeoutRef.current);
              contactHideTimeoutRef.current = null;
            }
            setShowContactCard((s) => !s);
            setShowHeaderDetails(false);
            setShowOptions(false);
          }}
        >
          {mail.from}
        </span>

        <button
          ref={headerToggleRef}
          className="mail-meta-toggle"
          onClick={() => {
            setShowHeaderDetails((p) => !p);
            setShowOptions(false);
            setShowContactCard(false);
          }}
        >
          ▼
        </button>
      </div>
    </div>

  {/* RIGHT SIDE ACTIONS */}
  <div className="mail-preview-actions-right">
  <button
    ref={optionsToggleRef}
    className="mail-more-btn"
    onClick={() => {
      setShowOptions((s) => !s);
      setShowHeaderDetails(false);
      setShowContactCard(false);
    }}
    aria-haspopup="menu"
    aria-expanded={showOptions}
  >
    ⋮
  </button>

  {showOptions && (
    <div className="mail-header-overlay">
      <div
        className="mail-more-menu modal"
        ref={optionsRef}
        role="menu"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mail-more-item" onClick={onReply}>
          <i className="fa-solid fa-reply" />
          <span>Reply</span>
        </div>

        <div className="mail-more-item reply-all" onClick={onReplyAll}>
          <i className="fa-solid fa-reply-all" />
          <span>Reply All</span>
        </div>

        <div className="mail-more-item" onClick={onForward}>
          <i className="fa-solid fa-share" />
          <span>Forward</span>
        </div>

        <div className="mail-more-item" onClick={() => { setShowOptions(false); handlePrint(); }}>
          <i className="fa-solid fa-print" />
          <span>Print</span>
        </div>

        <div className="mail-more-sep" />

        <div className="mail-more-item danger" onClick={() => onDelete(mail.id)}>
          <i className="fa-solid fa-trash" />
          <span>{activeFolder === "trash" ? "Delete permanently" : "Move to Trash"}</span>
        </div>
      </div>
    </div>
  )}
</div>

</div>


   {showHeaderDetails && (
  <div className="mail-header-overlay">
      <div className="mail-header-details modal" ref={headerDetailsRef} role="dialog" aria-modal="true">
      <div><strong>From:</strong> {formatRecipientValue(mail.from)}</div>
      <div><strong>To:</strong> {formatRecipientValue(mail.to)}</div>
      {formatRecipientValue(mail.cc) && <div><strong>Cc:</strong> {formatRecipientValue(mail.cc)}</div>}
      {formatRecipientValue(mail.bcc || mail.bccRecipients || mail.bccList || mail.blindCarbonCopy) && (
        <div><strong>Bcc:</strong> {formatRecipientValue(mail.bcc || mail.bccRecipients || mail.bccList || mail.blindCarbonCopy)}</div>
      )}
      <div>
        <strong>Date:</strong>{" "}
        {formatDate(mail.sentAt || mail.date)}
      </div>
      <div><strong>Subject:</strong> {mail.subject}</div>
      {mail.mailedBy && <div><strong>Mailed-by:</strong> {mail.mailedBy}</div>}
      {mail.signedBy && <div><strong>Signed-by:</strong> {mail.signedBy}</div>}
      {mail.mailStatus !== undefined && (
        <div><strong>Status:</strong> 
          <select className="selectdivstatus"
  value={mail.mailStatus || "NA"}
  onChange={async (e) => {
    try {
      const newStatus = e.target.value;

      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      const headers = parsed?.token
        ? { Authorization: `Bearer ${parsed.token}` }
        : null;

      await updateMailStatus(mail.id, newStatus, headers);

      // 🔥 IMPORTANT: notify parent
      if (typeof onStatusUpdated === "function") {
        onStatusUpdated(mail.id, newStatus);
      }

    } catch (err) {
      alert("Failed to update status");
    }
  }}
>
            <option value="Open">Open</option>
            <option value="Action">Action</option>
            <option value="In Progress">In Progress</option>
            <option value="Closed">Closed</option>
          </select>
        </div>
      )}
      <div>
        <strong>Security:</strong>{" "}
        {mail.secure ? "AES-256-GCM encrypted" : "End to End Encrypted"}
      </div>
    </div>
  </div>
)}

  {/* Contact popover shown when hovering/clicking the sender */}
  {showContactCard && (
    <div
      className="contact-popover"
      onMouseEnter={() => {
        if (contactHideTimeoutRef.current) {
          clearTimeout(contactHideTimeoutRef.current);
          contactHideTimeoutRef.current = null;
        }
        setShowContactCard(true);
      }}
      onMouseLeave={() => {
        contactHideTimeoutRef.current = setTimeout(() => {
          setShowContactCard(false);
          contactHideTimeoutRef.current = null;
        }, 220);
      }}
      style={{ position: "absolute", top: contactAnchorRef.current ? (contactAnchorRef.current.getBoundingClientRect().bottom + window.scrollY + 8) : 0, left: contactAnchorRef.current ? (contactAnchorRef.current.getBoundingClientRect().left + window.scrollX) : 0 }}
    >
      <div className="contact-popover-row">
        <div className="contact-avatar">{/* avatar: use profile image if available, otherwise initials */}
          { /* prefer mail.profileImageUrl or fallback to generated initials */ }
          {(profileImage || mail.profileImageUrl || mail.profileImage) ? (
            <img src={profileImage || mail.profileImageUrl || mail.profileImage} alt="avatar" />
          ) : (
            <div className="contact-initials">{((profileName || mail.fromName || mail.from) || "").split(" ").map(s=>s[0]).slice(0,2).join("").toUpperCase()}</div>
          )}
        </div>
        <div className="contact-info">
          <div className="contact-name">{profileName || mail.fromName || mail.from}</div>
          <div className="contact-email">{mail.from}</div>
        </div>
      </div>

      <div className="contact-actions">
        <button className="button" onClick={() => handleSendMail(mail.from)}><span className="fa fa-envelope"></span></button>
        <button className="button" onClick={() => handleAddContact(mail.from)}><span className="fa fa-user-plus"></span></button>
        <button className="button" onClick={() => handleChat(mail.from)}><span className="fa fa-comments"></span></button>
      </div>
      {contactActionMsg && <div className="contact-msg">{contactActionMsg}</div>}
    </div>
  )}


      {attachments.length > 0 && (
        <div className="mail-preview-tabs">
          <button type="button" className={`mail-preview-tab ${previewTab === "message" ? "active" : ""}`} onClick={() => setPreviewTab("message")}>
            Message
          </button>
          <button type="button" className={`mail-preview-tab ${previewTab === "attachment" ? "active" : ""}`} onClick={() => setPreviewTab("attachment")}>
            Attachment
          </button>
        </div>
      )}

      {(attachments.length ? previewTab === "message" : true) && (
        <div
          className="mail-preview-body"
          dangerouslySetInnerHTML={{
            __html:
              mail.body && mail.body.trim().length
                ? mail.body
                : "<em>(No content)</em>",
          }}
        />
      )}

      {previewTab === "attachment" && (
        attachments.length ? (
          <div className="mail-preview-attachments">
            {attachments.map((attachment) => (
              <div key={attachment.id || attachment.url} className="mail-preview-attachment">
                <div className="mail-preview-attachment-main">
                  <a
                    href={resolveAttachmentHref(SERVER_URL, attachment.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mail-attachment-link"
                  >
                    {attachment.name || "Download attachment"}
                  </a>
                  {typeof attachment.size === "number" && <span className="mail-attachment-size">{formatBytes(attachment.size)}</span>}
                  {attachment.mimeType && <span className="mail-attachment-mime">{attachment.mimeType}</span>}
                </div>
                {attachment.mimeType?.startsWith("image/") && (
                  <div className="mail-preview-attachment-thumb">
                    <img src={resolveAttachmentHref(SERVER_URL, attachment.url)} alt={attachment.name || "Attachment"} loading="lazy" />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="mail-preview-empty">No attachments</div>
        )
      )}
    </div>
  );
}

export default MailPreview;
